import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  FaPlus, FaStar, FaRegStar, FaTrash, FaTimes, FaChevronDown,
  FaCheckSquare, FaChevronLeft, FaChevronRight, FaCheck, FaInbox,
} from "react-icons/fa";
import * as api from "../api/checklistApi";

/* ─────────── design tokens ───────────
   A "working ledger" palette: cool paper background, ink text, a single
   confident teal accent (the "stamp ink" color used for the active state,
   the primary button, and the checkmark), amber reserved only for stars,
   and rose reserved only for danger/overdue — so color always means
   something instead of decorating. */
const T = {
  bg: "#f4f6f8",
  white: "#ffffff",
  border: "#e4e8ee",
  borderStrong: "#d7dce4",
  shadow: "0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)",
  shadowMd: "0 12px 32px -8px rgba(16,24,40,0.22)",
  radius: 14,
  ink1: "#101828",
  ink2: "#475467",
  ink3: "#98a2b3",
  primary: "#6366f1",
  primaryDark: "#4f46e5",
  primarySoft: "#eef2ff",
  amber: "#f59e0b",
  danger: "#e11d48",
  dangerSoft: "#fef2f2",
  todaySoft: "#fffbeb",
  todayFg: "#b45309",
  fontDisplay: "'Space Grotesk', 'Segoe UI', ui-sans-serif, sans-serif",
  fontBody: "'Inter', 'Segoe UI', ui-sans-serif, sans-serif",
  fontMono: "'IBM Plex Mono', ui-monospace, monospace",
};

const REPEAT_OPTIONS = [
  { value: "NONE", label: "Does not repeat" },
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "YEARLY", label: "Yearly" },
];

const FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
];

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function parseYmd(ymd) {
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function toYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
function formatDateDisplay(ymd) {
  const d = parseYmd(ymd);
  return d ? `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}` : "";
}

function generateTimeOptions() {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      const period = h < 12 ? "AM" : "PM";
      const displayHour = h % 12 === 0 ? 12 : h % 12;
      opts.push({ value, label: `${displayHour}:${String(m).padStart(2, "0")} ${period}` });
    }
  }
  return opts;
}
const TIME_OPTIONS = generateTimeOptions();
function formatTimeDisplay(hhmm) {
  return TIME_OPTIONS.find((o) => o.value === hhmm)?.label || hhmm;
}

/* Derives real urgency from the item's own due date/time — no invented
   data — so the badge color always encodes something true. */
function getDueStatus(item) {
  if (!item.dueDate || item.completed) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = parseYmd(item.dueDate);
  if (item.dueTime && !item.allDay) {
    const dueDateTime = new Date(`${item.dueDate}T${item.dueTime}`);
    if (dueDateTime.getTime() < Date.now()) return "overdue";
  } else if (due.getTime() < today.getTime()) {
    return "overdue";
  }
  if (due.getTime() === today.getTime()) return "today";
  return "upcoming";
}
const DUE_BADGE = {
  overdue: { bg: T.dangerSoft, fg: T.danger, border: "transparent" },
  today: { bg: T.todaySoft, fg: T.todayFg, border: "transparent" },
  upcoming: { bg: "#f1f5f9", fg: T.ink2, border: "transparent" },
};

/* Deterministic color tag per list, purely for quick visual recognition
   in the sidebar — same idea as color-coded folders. */
function listAccent(seed) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = seed.charCodeAt(i) + ((h << 5) - h);
  const hue = Math.abs(h) % 360;
  return `hsl(${hue}, 58%, 45%)`;
}

/* One shared stylesheet: font import, focus rings, and the hover-reveal
   pattern for row actions (can't be done with inline styles). */
function GlobalStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500&display=swap');
      @keyframes checklist-spin { to { transform: rotate(360deg); } }
      @keyframes checklist-fade-in { from { opacity: 0; transform: translateY(6px) scale(.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
      .chk-row-actions { opacity: 0; transition: opacity .12s ease; }
      .chk-row:hover .chk-row-actions { opacity: 1; }
      .chk-row:hover { background: #fafbfc; }
      .chk-btn:focus-visible, .chk-icon:focus-visible, .chk-pill:focus-visible, .chk-day:focus-visible {
        outline: 2px solid ${T.primary}; outline-offset: 2px;
      }
      .chk-side-item:hover { background: #f1f4f7; }
      .chk-side-item.active:hover { background: #e6f6f4; }
      * { box-sizing: border-box; }
    `}</style>
  );
}

/* ─────────── generic click-outside pill dropdown (no native <select>) ─────────── */
function PillSelect({ value, options, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        className="chk-btn"
        onClick={() => setOpen((p) => !p)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${T.border}`,
          background: T.white, color: T.ink1, fontSize: 13.5, fontFamily: T.fontBody, cursor: "pointer",
          transition: "border-color .12s ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = T.borderStrong)}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = T.border)}
      >
        <span>{selected ? selected.label : <span style={{ color: T.ink3 }}>{placeholder}</span>}</span>
        <FaChevronDown size={10} style={{ color: T.ink3, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 40,
          background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: T.shadowMd,
          maxHeight: 220, overflowY: "auto", padding: 4, animation: "checklist-fade-in .12s ease",
        }}>
          {options.map((o) => (
            <div
              key={o.value}
              onClick={() => { onChange(o.value); setOpen(false); }}
              style={{
                padding: "8px 10px", borderRadius: 7, fontSize: 13.5, cursor: "pointer",
                color: o.value === value ? T.primary : T.ink1,
                background: o.value === value ? T.primarySoft : "transparent",
                fontWeight: o.value === value ? 600 : 400,
              }}
              onMouseEnter={(e) => { if (o.value !== value) e.currentTarget.style.background = "#f8fafc"; }}
              onMouseLeave={(e) => { if (o.value !== value) e.currentTarget.style.background = "transparent"; }}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────── custom calendar date picker (no native <input type=date>) ─────────── */
function DatePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selectedDate = parseYmd(value);
  const [viewDate, setViewDate] = useState(selectedDate || new Date());
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (open) setViewDate(selectedDate || new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const startWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();
  const totalCells = Math.ceil((startWeekday + daysInMonth) / 7) * 7;

  const cells = [];
  for (let i = 0; i < totalCells; i++) {
    const dayNum = i - startWeekday + 1;
    if (dayNum < 1) cells.push({ day: daysInPrevMonth + dayNum, inMonth: false, date: new Date(year, month - 1, daysInPrevMonth + dayNum) });
    else if (dayNum > daysInMonth) cells.push({ day: dayNum - daysInMonth, inMonth: false, date: new Date(year, month + 1, dayNum - daysInMonth) });
    else cells.push({ day: dayNum, inMonth: true, date: new Date(year, month, dayNum) });
  }

  const isSelected = (d) => selectedDate && d.toDateString() === selectedDate.toDateString();
  const isToday = (d) => d.toDateString() === new Date().toDateString();

  return (
    <div ref={ref} style={{ position: "relative", flex: 1 }}>
      <button
        type="button"
        className="chk-btn"
        onClick={() => setOpen((p) => !p)}
        style={{ ...pillInputStyle, width: "100%", textAlign: "left", cursor: "pointer", color: value ? T.ink1 : T.ink3 }}
      >
        {value ? formatDateDisplay(value) : "Add date"}
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 50, background: T.white,
          border: `1px solid ${T.border}`, borderRadius: 14, boxShadow: T.shadowMd, padding: 14, width: 280,
          animation: "checklist-fade-in .14s ease",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ fontWeight: 700, fontSize: 13.5, color: T.ink1, fontFamily: T.fontDisplay }}>{MONTH_NAMES[month]} {year}</span>
            <div style={{ display: "flex", gap: 2 }}>
              <button type="button" className="chk-icon" onClick={() => setViewDate(new Date(year, month - 1, 1))} style={navArrowStyle}><FaChevronLeft size={11} /></button>
              <button type="button" className="chk-icon" onClick={() => setViewDate(new Date(year, month + 1, 1))} style={navArrowStyle}><FaChevronRight size={11} /></button>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", textAlign: "center" }}>
            {WEEKDAYS.map((w, i) => (
              <div key={i} style={{ fontSize: 10.5, color: T.ink3, fontWeight: 700, padding: "4px 0" }}>{w}</div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
            {cells.map((c, i) => (
              <div
                key={i}
                onClick={() => { onChange(toYmd(c.date)); setOpen(false); }}
                style={{
                  textAlign: "center", padding: "7px 0", borderRadius: "50%", fontSize: 12.5, cursor: "pointer",
                  color: !c.inMonth ? T.ink3 : isSelected(c.date) ? "#fff" : T.ink1,
                  background: isSelected(c.date) ? T.primary : "transparent",
                  fontWeight: isToday(c.date) && !isSelected(c.date) ? 700 : 400,
                  opacity: !c.inMonth ? 0.4 : 1,
                  border: isToday(c.date) && !isSelected(c.date) ? `1px solid ${T.primary}` : "1px solid transparent",
                }}
                onMouseEnter={(e) => { if (!isSelected(c.date)) e.currentTarget.style.background = "#f1f5f9"; }}
                onMouseLeave={(e) => { if (!isSelected(c.date)) e.currentTarget.style.background = "transparent"; }}
              >
                {c.day}
              </div>
            ))}
          </div>
          {value && (
            <div style={{ marginTop: 8, textAlign: "right" }}>
              <button type="button" onClick={() => { onChange(""); setOpen(false); }} style={{ ...textBtnStyle, fontSize: 12 }}>Clear date</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────── custom scrollable time picker (no native <input type=time>) ─────────── */
function TimePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (open && listRef.current) {
      const idx = TIME_OPTIONS.findIndex((o) => o.value === value);
      listRef.current.children[idx >= 0 ? idx : 0]?.scrollIntoView({ block: "center" });
    }
  }, [open, value]);

  return (
    <div ref={ref} style={{ position: "relative", flex: 1 }}>
      <button
        type="button"
        className="chk-btn"
        onClick={() => setOpen((p) => !p)}
        style={{ ...pillInputStyle, width: "100%", textAlign: "left", cursor: "pointer", color: value ? T.ink1 : T.ink3 }}
      >
        {value ? formatTimeDisplay(value) : "Add time"}
      </button>
      {open && (
        <div ref={listRef} style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 50,
          background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, boxShadow: T.shadowMd,
          maxHeight: 220, overflowY: "auto", padding: 4, animation: "checklist-fade-in .12s ease",
        }}>
          {TIME_OPTIONS.map((o) => (
            <div
              key={o.value}
              onClick={() => { onChange(o.value); setOpen(false); }}
              style={{
                padding: "9px 14px", borderRadius: 7, fontSize: 13, cursor: "pointer", fontFamily: T.fontMono,
                color: o.value === value ? "#fff" : T.ink1,
                background: o.value === value ? T.primary : "transparent",
                fontWeight: o.value === value ? 600 : 400,
              }}
              onMouseEnter={(e) => { if (o.value !== value) e.currentTarget.style.background = "#f1f5f9"; }}
              onMouseLeave={(e) => { if (o.value !== value) e.currentTarget.style.background = "transparent"; }}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* Animated checkbox — a small stroke-draw checkmark instead of an inert
   filled circle, so completing a task gives a moment of feedback. */
function CheckCircle({ checked, onClick, size = 20 }) {
  return (
    <span
      onClick={onClick}
      className="chk-icon"
      role="checkbox"
      aria-checked={checked}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); } }}
      style={{
        width: size, height: size, borderRadius: "50%",
        border: `2px solid ${checked ? T.primary : T.ink3}`,
        background: checked ? T.primary : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, cursor: "pointer", transition: "background .16s ease, border-color .16s ease",
      }}
    >
      <svg width={size * 0.55} height={size * 0.45} viewBox="0 0 11 9" fill="none">
        <path
          d="M1 4.5L4 7.5L10 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ strokeDasharray: 14, strokeDashoffset: checked ? 0 : 14, transition: "stroke-dashoffset .22s ease .04s" }}
        />
      </svg>
    </span>
  );
}

/* ─────────── item edit / create modal ─────────── */
function ItemModal({ item, lists, defaultListId, onClose, onSaved }) {
  const isEdit = !!item;
  const [title, setTitle] = useState(item?.title || "");
  const [description, setDescription] = useState(item?.description || "");
  const [dueDate, setDueDate] = useState(item?.dueDate || "");
  const [dueTime, setDueTime] = useState(item?.dueTime ? item.dueTime.slice(0, 5) : "");
  const [allDay, setAllDay] = useState(item?.allDay || false);
  const [repeatRule, setRepeatRule] = useState(item?.repeatRule || "NONE");
  const [listId, setListId] = useState(item?.listId || defaultListId);
  const [saving, setSaving] = useState(false);

  const listOptions = lists.map((l) => ({ value: l.id, label: l.name }));

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        dueDate: dueDate || null,
        dueTime: allDay || !dueTime ? null : `${dueTime}:00`,
        allDay,
        repeatRule,
        listId,
      };
      if (isEdit) {
        await api.updateItem(item.id, payload);
      } else {
        await api.createItem(payload);
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.message || "Failed to save item");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalStyle, maxWidth: 460 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 13, fontWeight: 600, color: T.ink3, letterSpacing: ".3px", textTransform: "uppercase" }}>
            {isEdit ? "Edit item" : "New item"}
          </span>
          <button type="button" className="chk-icon" onClick={onClose} style={iconBtnStyle}><FaTimes /></button>
        </div>

        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSave()}
          placeholder="Add title"
          style={{
            width: "100%", border: "none", borderBottom: `2px solid ${T.primary}`,
            fontFamily: T.fontDisplay, fontSize: 19, fontWeight: 600, padding: "8px 2px 10px",
            outline: "none", color: T.ink1, marginBottom: 18, background: "transparent",
          }}
        />

        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <DatePicker value={dueDate} onChange={setDueDate} />
          {!allDay && <TimePicker value={dueTime} onChange={setDueTime} />}
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, color: T.ink2, marginBottom: 16, cursor: "pointer", userSelect: "none" }}>
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} style={{ accentColor: T.primary, width: 15, height: 15 }} />
          All day
        </label>

        <div style={{ marginBottom: 14 }}>
          <PillSelect value={repeatRule} options={REPEAT_OPTIONS} onChange={setRepeatRule} placeholder="Does not repeat" />
        </div>

        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Add description"
          rows={3}
          style={{
            width: "100%", border: `1px solid ${T.border}`, borderRadius: 10, padding: 11,
            fontSize: 13.5, color: T.ink1, resize: "vertical", marginBottom: 14, fontFamily: T.fontBody, outline: "none",
          }}
          onFocus={(e) => (e.currentTarget.style.borderColor = T.primary)}
          onBlur={(e) => (e.currentTarget.style.borderColor = T.border)}
        />

        <div style={{ marginBottom: 22 }}>
          <PillSelect value={listId} options={listOptions} onChange={setListId} placeholder="Select list" />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <button type="button" onClick={onClose} style={textBtnStyle}>Cancel</button>
          <button
            type="button"
            className="chk-btn"
            disabled={saving || !title.trim()}
            onClick={handleSave}
            style={{
              ...primaryBtnStyle,
              opacity: saving || !title.trim() ? 0.55 : 1,
              cursor: saving || !title.trim() ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────── create-list modal ─────────── */
function CreateListModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleDone = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.createList(name.trim());
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e.message || "Failed to create list");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalStyle, maxWidth: 360 }}>
        <h3 style={{ margin: "0 0 16px", fontSize: 17, fontFamily: T.fontDisplay, fontWeight: 700, color: T.ink1 }}>Create list</h3>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleDone()}
          placeholder="e.g. Groceries, Work, Home renovation"
          style={{
            width: "100%", border: "none", borderBottom: `2px solid ${T.primary}`,
            fontSize: 15, padding: "6px 2px 10px", outline: "none", color: T.ink1, marginBottom: 22,
            fontFamily: T.fontBody, background: "transparent",
          }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 16 }}>
          <button type="button" onClick={onClose} style={textBtnStyle}>Cancel</button>
          <button
            type="button"
            disabled={saving || !name.trim()}
            onClick={handleDone}
            style={{ ...textBtnStyle, color: T.primary, fontWeight: 700, opacity: !name.trim() ? 0.5 : 1 }}
          >
            {saving ? "Creating…" : "Create list"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────── spinner ─────────── */
function Spinner({ size = 30, padding = "60px 0" }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding }}>
      <div style={{
        width: size, height: size, borderRadius: "50%",
        border: `3px solid ${T.border}`, borderTopColor: T.primary,
        animation: "checklist-spin 0.7s linear infinite",
      }} />
    </div>
  );
}

/* ─────────── confirm modal ─────────── */
function ConfirmModal({ title, message, confirmLabel = "Delete", onCancel, onConfirm }) {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel, busy]);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={{ ...modalStyle, maxWidth: 360 }}>
        <h3 style={{ margin: "0 0 10px", fontSize: 17, fontFamily: T.fontDisplay, fontWeight: 700, color: T.ink1 }}>{title}</h3>
        <p style={{ margin: "0 0 22px", fontSize: 13.5, color: T.ink2, lineHeight: 1.55 }}>{message}</p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 16 }}>
          <button type="button" onClick={onCancel} disabled={busy} style={textBtnStyle}>Cancel</button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            style={{
              background: T.danger, color: "#fff", border: "none", borderRadius: 9,
              padding: "10px 18px", fontSize: 13.5, fontWeight: 600, fontFamily: T.fontBody,
              cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? "Deleting…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────── shared styles ─────────── */
const overlayStyle = {
  position: "fixed", inset: 0, background: "rgba(16,24,40,0.5)", backdropFilter: "blur(2px)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5000,
};
const modalStyle = {
  width: "90%", background: T.white, borderRadius: T.radius, boxShadow: T.shadowMd, padding: 26,
  fontFamily: T.fontBody, animation: "checklist-fade-in .16s ease",
};
const iconBtnStyle = {
  border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6, borderRadius: 8,
};
const pillInputStyle = {
  padding: "10px 12px", borderRadius: 10, border: `1px solid ${T.border}`, fontSize: 13.5, color: T.ink1,
  background: T.white, fontFamily: T.fontBody,
};
const navArrowStyle = {
  border: "none", background: "transparent", color: T.ink2, cursor: "pointer",
  padding: 6, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
};
const primaryBtnStyle = {
  background: T.primary, color: "#fff", border: "none", borderRadius: 9, padding: "10px 20px",
  fontSize: 13.5, fontWeight: 600, fontFamily: T.fontBody,
};
const textBtnStyle = {
  background: "transparent", border: "none", color: T.ink2, fontSize: 13.5, fontWeight: 600,
  cursor: "pointer", fontFamily: T.fontBody, padding: "10px 6px",
};

/* ─────────── main page ─────────── */
export default function ChecklistPage() {
  const [lists, setLists] = useState([]);
  const [activeView, setActiveView] = useState({ type: "all" }); // {type:'all'|'starred'|'list', listId?}
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null); // { type: 'item'|'list', id, label }
  const [filterMode, setFilterMode] = useState("all"); // 'all' | 'active' | 'completed' — client-side only

  const loadLists = useCallback(async () => {
    try {
      const data = await api.getLists();
      setLists(data);
      return data;
    } catch (e) {
      toast.error(e.message || "Failed to load lists");
      return [];
    }
  }, []);

  const loadItems = useCallback(async (view) => {
    setLoading(true);
    try {
      let data;
      if (view.type === "starred") data = await api.getStarredItems();
      else if (view.type === "list") data = await api.getItemsByList(view.listId);
      else data = await api.getAllItems();
      setItems(data);
    } catch (e) {
      toast.error(e.message || "Failed to load items");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const l = await loadLists();
      if (l.length) setActiveView({ type: "list", listId: l[0].id });
    })();
  }, [loadLists]);

  useEffect(() => {
    if (activeView.type !== "list" || activeView.listId) {
      loadItems(activeView);
    }
  }, [activeView, loadItems]);

  // Client-side "due soon" toast while this page is open, complementing the
  // server-side email reminder that fires even when the app is closed.
  const remindedRef = useRef(new Set());
  useEffect(() => {
    const checkReminders = async () => {
      try {
        const all = await api.getAllItems();
        const now = new Date();
        const soon = new Date(now.getTime() + 10 * 60000);
        all.forEach((item) => {
          if (item.completed || item.allDay || !item.dueDate || !item.dueTime) return;
          if (remindedRef.current.has(item.id)) return;
          const dueAt = new Date(`${item.dueDate}T${item.dueTime}`);
          if (dueAt > now && dueAt <= soon) {
            remindedRef.current.add(item.id);
            toast.info(`"${item.title}" is due at ${formatTimeDisplay(item.dueTime.slice(0, 5))}`, { autoClose: 8000 });
          }
        });
      } catch {
        // silent - background check, not user-initiated
      }
    };
    checkReminders();
    const interval = setInterval(checkReminders, 30000);
    return () => clearInterval(interval);
  }, []);

  const refresh = useCallback(async () => {
    const l = await loadLists();
    setLists(l);
    await loadItems(activeView);
  }, [activeView, loadLists, loadItems]);

  const defaultListId = useMemo(() => {
    if (activeView.type === "list") return activeView.listId;
    return lists[0]?.id;
  }, [activeView, lists]);

  const handleToggleComplete = async (item) => {
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, completed: !i.completed } : i)));
    try {
      await api.toggleComplete(item.id);
      refresh();
    } catch (e) {
      toast.error(e.message || "Failed to update item");
      refresh();
    }
  };

  const handleToggleStar = async (item, e) => {
    e.stopPropagation();
    try {
      await api.toggleStar(item.id);
      refresh();
    } catch (err) {
      toast.error(err.message || "Failed to update item");
    }
  };

  const handleDeleteItem = (item, e) => {
    e.stopPropagation();
    setConfirmTarget({ type: "item", id: item.id, label: item.title });
  };

  const handleDeleteList = (listId) => {
    const list = lists.find((l) => l.id === listId);
    setConfirmTarget({ type: "list", id: listId, label: list?.name || "this list" });
  };

  const handleConfirmedDelete = async () => {
    if (!confirmTarget) return;
    try {
      if (confirmTarget.type === "item") {
        await api.deleteItem(confirmTarget.id);
      } else {
        await api.deleteList(confirmTarget.id);
        if (activeView.type === "list" && activeView.listId === confirmTarget.id) {
          setActiveView({ type: "all" });
        }
      }
      await refresh();
      setConfirmTarget(null);
    } catch (e) {
      toast.error(e.message || `Failed to delete ${confirmTarget.type}`);
    }
  };

  const viewTitle = activeView.type === "all"
    ? "All items"
    : activeView.type === "starred"
      ? "Starred"
      : (lists.find((l) => l.id === activeView.listId)?.name || "");

  const visibleItems = items.filter((i) => {
    if (filterMode === "active") return !i.completed;
    if (filterMode === "completed") return i.completed;
    return true;
  });
  const openCount = items.filter((i) => !i.completed).length;

  return (
    <div style={{ display: "flex", flex: 1, minHeight: 0, background: T.bg, fontFamily: T.fontBody }}>
      <GlobalStyles />

      {/* Sidebar */}
      <div style={{ width: 250, borderRight: `1px solid ${T.border}`, background: T.white, padding: "18px 12px", flexShrink: 0, overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22, padding: "0 4px" }}>
          <div style={{
            width: 30, height: 30, borderRadius: 9, background: T.primary, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <FaCheck size={13} color="#fff" />
          </div>
          <span style={{ fontSize: 17, fontWeight: 700, color: T.ink1, fontFamily: T.fontDisplay }}>Checklist</span>
        </div>

        <button
          type="button"
          className="chk-btn"
          onClick={() => { setEditItem(null); setShowItemModal(true); }}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "10px 18px",
            borderRadius: 10, border: "none", background: T.primary, color: "#fff", fontWeight: 600,
            fontSize: 13.5, cursor: "pointer", marginBottom: 22, fontFamily: T.fontBody,
            boxShadow: "0 1px 2px rgba(99,102,241,0.3)", transition: "background .12s ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = T.primaryDark)}
          onMouseLeave={(e) => (e.currentTarget.style.background = T.primary)}
        >
          <FaPlus size={11} /> Create 
        </button>

        <SideItem
          active={activeView.type === "all"}
          icon={<FaCheckSquare />}
          label="All items"
          onClick={() => setActiveView({ type: "all" })}
        />
        <SideItem
          active={activeView.type === "starred"}
          icon={<FaRegStar />}
          label="Starred"
          onClick={() => setActiveView({ type: "starred" })}
        />

        <div style={{ marginTop: 20, marginBottom: 6, fontSize: 11, fontWeight: 700, color: T.ink3, textTransform: "uppercase", letterSpacing: ".6px", padding: "0 10px", fontFamily: T.fontDisplay }}>
          Lists
        </div>
        {lists.map((l) => (
          <SideItem
            key={l.id}
            active={activeView.type === "list" && activeView.listId === l.id}
            icon={<span style={{ width: 8, height: 8, borderRadius: "50%", background: listAccent(l.name || String(l.id)), display: "inline-block" }} />}
            label={l.name}
            count={l.openItemCount}
            onDelete={lists.length > 1 ? () => handleDeleteList(l.id) : null}
            onClick={() => setActiveView({ type: "list", listId: l.id })}
          />
        ))}

        <div
          onClick={() => setShowListModal(true)}
          className="chk-side-item"
          style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 10px", borderRadius: 8, cursor: "pointer", color: T.ink2, fontSize: 13.5, fontWeight: 600, marginTop: 4 }}
        >
          <FaPlus size={11} /> Create list
        </div>
      </div>

      {/* Main panel */}
      <div style={{ flex: 1, padding: "26px 34px", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 14, marginBottom: 6 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 24, color: T.ink1, fontWeight: 700, fontFamily: T.fontDisplay }}>{viewTitle}</h2>
            <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 4, fontFamily: T.fontMono }}>
              {loading ? "loading…" : `${openCount} open · ${items.length} total`}
            </div>
          </div>

          <button
            type="button"
            className="chk-btn"
            onClick={() => { setEditItem(null); setShowItemModal(true); }}
            style={{
              display: "flex", alignItems: "center", gap: 8, padding: "9px 16px",
              borderRadius: 9, border: `1px solid ${T.border}`, background: T.white, color: T.ink1,
              fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: T.fontBody,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = T.primary)}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = T.border)}
          >
            <FaPlus size={11} color={T.primary} /> Add item
          </button>
        </div>

        {/* Filter chips */}
        <div style={{ display: "flex", gap: 6, margin: "16px 0 18px" }}>
          {FILTER_OPTIONS.map((f) => {
            const active = filterMode === f.value;
            return (
              <button
                key={f.value}
                type="button"
                className="chk-pill"
                onClick={() => setFilterMode(f.value)}
                style={{
                  padding: "6px 14px", borderRadius: 20, fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                  border: `1px solid ${active ? T.primary : T.border}`,
                  background: active ? T.primarySoft : T.white,
                  color: active ? T.primary : T.ink2,
                  fontFamily: T.fontBody, transition: "all .12s ease",
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <Spinner />
        ) : visibleItems.length === 0 ? (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
            color: T.ink3, marginTop: 56, textAlign: "center",
          }}>
            <FaInbox size={26} style={{ color: T.borderStrong }} />
            <div style={{ fontSize: 14, color: T.ink2, fontWeight: 600, fontFamily: T.fontDisplay }}>
              {items.length === 0 ? "Nothing here yet" : "No items match this filter"}
            </div>
            <div style={{ fontSize: 13, maxWidth: 260 }}>
              {items.length === 0 ? "Add your first item to get this list moving." : "Try a different filter, or add a new item."}
            </div>
          </div>
        ) : (
          <div style={{ background: T.white, borderRadius: T.radius, boxShadow: T.shadow, overflow: "hidden", border: `1px solid ${T.border}` }}>
            {visibleItems.map((item, idx) => {
              const status = getDueStatus(item);
              const badge = status ? DUE_BADGE[status] : null;
              return (
                <div
                  key={item.id}
                  className="chk-row"
                  onClick={() => { setEditItem(item); setShowItemModal(true); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 13, padding: "14px 16px",
                    borderBottom: idx === visibleItems.length - 1 ? "none" : `1px solid ${T.border}`,
                    cursor: "pointer", borderLeft: `3px solid ${status === "overdue" ? T.danger : status === "today" ? T.amber : "transparent"}`,
                    transition: "background .1s ease",
                  }}
                >
                  <CheckCircle checked={item.completed} onClick={(e) => { e.stopPropagation(); handleToggleComplete(item); }} />

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 14, color: item.completed ? T.ink3 : T.ink1, fontWeight: 500,
                      textDecoration: item.completed ? "line-through" : "none",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {item.title}
                    </div>
                    {(item.dueDate || item.description) && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                        {item.dueDate && (
                          <span style={{
                            fontSize: 11, fontFamily: T.fontMono, fontWeight: 500, padding: "2px 7px", borderRadius: 6,
                            background: badge ? badge.bg : "#f1f5f9", color: badge ? badge.fg : T.ink3,
                          }}>
                            {formatDateDisplay(item.dueDate)}{item.dueTime && !item.allDay ? ` · ${formatTimeDisplay(item.dueTime.slice(0, 5))}` : ""}
                          </span>
                        )}
                        {item.description && (
                          <span style={{ fontSize: 12, color: T.ink3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {item.description}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="chk-row-actions" style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <span onClick={(e) => handleToggleStar(item, e)} className="chk-icon" style={{ color: item.starred ? T.amber : T.ink3, cursor: "pointer", opacity: item.starred ? 1 : undefined }}>
                      {item.starred ? <FaStar /> : <FaRegStar />}
                    </span>
                    <span onClick={(e) => handleDeleteItem(item, e)} className="chk-icon" style={{ color: T.ink3, cursor: "pointer" }}>
                      <FaTrash size={13} />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showItemModal && (
        <ItemModal
          item={editItem}
          lists={lists}
          defaultListId={defaultListId}
          onClose={() => setShowItemModal(false)}
          onSaved={refresh}
        />
      )}
      {showListModal && (
        <CreateListModal onClose={() => setShowListModal(false)} onCreated={refresh} />
      )}
      {confirmTarget && (
        <ConfirmModal
          title={confirmTarget.type === "list" ? "Delete list?" : "Delete item?"}
          message={
            confirmTarget.type === "list"
              ? `"${confirmTarget.label}" and all of its items will be permanently deleted.`
              : `"${confirmTarget.label}" will be permanently deleted.`
          }
          onCancel={() => setConfirmTarget(null)}
          onConfirm={handleConfirmedDelete}
        />
      )}
    </div>
  );
}

function SideItem({ active, icon, label, count, onClick, onDelete }) {
  return (
    <div
      onClick={onClick}
      className={`chk-side-item${active ? " active" : ""}`}
      style={{
        display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 9,
        cursor: "pointer", fontSize: 13.5, fontWeight: active ? 700 : 500,
        color: active ? T.primary : T.ink2, background: active ? T.primarySoft : "transparent",
        marginBottom: 2, transition: "background .1s ease",
      }}
    >
      <span style={{ fontSize: 13, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", width: 14 }}>{icon}</span>
      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      {typeof count === "number" && count > 0 && (
        <span style={{ fontSize: 11, color: active ? T.primary : T.ink3, fontFamily: T.fontMono, fontWeight: 600 }}>{count}</span>
      )}
      {onDelete && (
        <span
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          title="Delete list"
          className="chk-icon"
          style={{ color: T.ink3, opacity: 0.7 }}
        >
          <FaTrash size={11} />
        </span>
      )}
    </div>
  );
}